import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, FileText, Loader2, Mail, Phone, Briefcase, User,
  Save, Send, MapPin, Building2, Calendar, DollarSign, Users, Award,
  Clock, Shield, ClipboardList, CheckCircle2, Pencil, Eye, EyeOff, ChevronUp, ChevronDown, Plus, X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import CustomEditor from "../../components/ui/custom-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { formatSydneyDateLong } from "../../utils/helpers";
import { Calendar as CalendarPrimitive } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";

interface JobPosition {
  id: number;
  positionCode: string;
  location: string | null;
  employmentType: string | null;
}

interface Candidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  currentPosition: string | null;
  requisition: {
    id: number;
    jobRole: { id: number; roleName: string } | null;
    department: { id: number; departmentName: string } | null;
    reportsTo: { id: number; name: string } | null;
    jobPositions: JobPosition[];
    salaryRangeMin: number | null;
    salaryRangeMax: number | null;
    startDate: string | null;
  } | null;
  jobPosition: JobPosition | null;
}

interface OfferTemplate {
  id: number;
  name: string;
  subject: string | null;
  bodyContent: string | null;
  isDefault: boolean;
  isActive: boolean;
  isSystem: boolean;
  templateType: string | null;
  customFields: { label: string; type: string; required: boolean }[];
}

interface RequisitionOrganization {
  id: number;
  name: string;
  companyName: string;
  defaultOfferExpiryDays: number;
  isDefault: boolean;
}

interface Offer {
  id: number;
  status: string;
  salaryOffered: number | string | null;
  startDate: string | null;
  expiryDate: string | null;
  offerLetterContent: string | null;
  offerTemplate: { id: number; name: string } | null;
  requisitionOrganization: { id: number; name: string; companyName: string } | null;
}

type Step = "select-template" | "fill-form";

interface NavField {
  key: string;
  label: string;
  editable: boolean;
  sectionId: string;
}

// Extracted to top level so React doesn't unmount/remount on every parent re-render
const FieldRow = ({
  children,
  fieldKey,
  label,
  isActive,
  isHidden,
  onToggleHidden,
  required,
}: {
  children: React.ReactNode;
  fieldKey: string;
  label: string;
  isActive: boolean;
  isHidden: boolean;
  onToggleHidden: (key: string) => void;
  required?: boolean;
}) => {
  return (
    <div
      id={`field-${fieldKey}`}
      className={`relative group rounded-xl p-2 -m-2 transition-all ${isActive ? "bg-blue-50/40 ring-1 ring-blue-200" : "hover:bg-gray-50/50"} ${isHidden ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-bold text-gray-900">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <button
          onClick={() => onToggleHidden(fieldKey)}
          className={`p-1 rounded-md transition-all opacity-0 group-hover:opacity-100 ${
            isHidden
              ? "text-gray-300 hover:text-blue-500 hover:bg-blue-50"
              : "text-gray-400 hover:text-blue-500 hover:bg-blue-50"
          }`}
          title={isHidden ? "Show in preview" : "Hide from preview"}
        >
          {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
      {children}
    </div>
  );
};

const NAV_FIELDS: NavField[] = [
  { key: "jobPosition", label: "Job Position", editable: true, sectionId: "preview-section-position" },
  { key: "candidateName", label: "Candidate Name", editable: false, sectionId: "preview-section-greeting" },
  { key: "employeeAddress", label: "Employee Address", editable: true, sectionId: "preview-section-greeting" },
  { key: "positionTitle", label: "Position Title", editable: false, sectionId: "preview-section-position" },
  { key: "companyName", label: "Company Name", editable: true, sectionId: "preview-section-position" },
  { key: "abn", label: "ABN", editable: true, sectionId: "preview-section-position" },
  { key: "companyLocation", label: "Company Location", editable: true, sectionId: "preview-section-position" },
  { key: "department", label: "Department", editable: false, sectionId: "preview-section-position" },
  { key: "location", label: "Location", editable: true, sectionId: "preview-section-details" },
  { key: "employmentType", label: "Employment Type", editable: true, sectionId: "preview-section-details" },
  { key: "employmentStatus", label: "Employment Status", editable: true, sectionId: "preview-section-details" },
  { key: "startDate", label: "Start Date", editable: true, sectionId: "preview-section-details" },
  { key: "salary", label: "Salary", editable: true, sectionId: "preview-section-details" },
  { key: "baseHourlyRate", label: "Base Hourly Rate", editable: true, sectionId: "preview-section-details" },
  { key: "remuneration", label: "Remuneration Package", editable: true, sectionId: "preview-section-details" },
  { key: "reportingTo", label: "Reporting To", editable: false, sectionId: "preview-section-details" },
  { key: "award", label: "Award", editable: true, sectionId: "preview-section-details" },
  { key: "hoursOfWork", label: "Hours of Work", editable: true, sectionId: "preview-section-details" },
  { key: "probationPeriod", label: "Probation Period", editable: true, sectionId: "preview-section-terms" },
  { key: "noticePeriod", label: "Notice Period", editable: true, sectionId: "preview-section-terms" },
  { key: "restraintArea", label: "Restraint Area", editable: true, sectionId: "preview-section-terms" },
  { key: "restraintPeriod", label: "Restraint Period", editable: true, sectionId: "preview-section-terms" },
  { key: "entitlements", label: "Entitlements", editable: true, sectionId: "preview-section-entitlements" },
  { key: "offerDeadline", label: "Offer Deadline", editable: true, sectionId: "preview-section-deadline" },
  { key: "directorsName", label: "Directors Name", editable: true, sectionId: "preview-section-signature" },
  { key: "senderName", label: "Sender Name", editable: true, sectionId: "preview-section-signature" },
  { key: "senderTitle", label: "Sender Title", editable: true, sectionId: "preview-section-signature" },
];

const formatSalary = (min: number | null, max: number | null) => {
  if (!min && !max) return "";
  const fmt = (n: number) => `$${n.toLocaleString()}`;
  if (min && max) return `${fmt(min)} - ${fmt(max)}`;
  return min ? fmt(min) : fmt(max!);
};

const formatDateInput = (dateStr: string | null) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString().split("T")[0];
};

const CreateOffer: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.UPDATE, RESOURCES.CANDIDATE_OFFER)) {
    return <AccessRestricted />;
  }
  const { id } = useParams();
  const candidateId = parseInt(id || "0");
  const { currentUser } = useAuth();

  const [step, setStep] = useState<Step>("select-template");
  const [activeFieldIndex, setActiveFieldIndex] = useState(0);
  const [hoveredFieldKey, setHoveredFieldKey] = useState<string | null>(null);

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [organizations, setOrganizations] = useState<RequisitionOrganization[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [offerId, setOfferId] = useState<number | null>(null);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedLetterContent, setEditedLetterContent] = useState<string | null>(null);
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
  const [showSendModal, setShowSendModal] = useState(false);

  // Form fields
  const [jobPositionId, setJobPositionId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [salary, setSalary] = useState("");
  const [award, setAward] = useState("");
  const [probationPeriod, setProbationPeriod] = useState("");
  const [noticePeriod, setNoticePeriod] = useState("");
  const [restraintArea, setRestraintArea] = useState("");
  const [restraintPeriod, setRestraintPeriod] = useState("");
  const [entitlements, setEntitlements] = useState<string[]>([
    "Health Insurance",
    "Retirement Plan",
    "Paid Time Off",
    "Professional Development",
  ]);
  const [offerDeadline, setOfferDeadline] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderTitle, setSenderTitle] = useState("");
  const [employeeAddress, setEmployeeAddress] = useState("");
  const [abn, setAbn] = useState("");
  const [companyLocation, setCompanyLocation] = useState("");
  const [baseHourlyRate, setBaseHourlyRate] = useState("");
  const [hoursOfWork, setHoursOfWork] = useState("");
  const [directorsName, setDirectorsName] = useState("");
  const [remuneration, setRemuneration] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("");

  // Refs
  const fieldRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLButtonElement | null)[]>([]);
  const previewRef = useRef<HTMLDivElement>(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const selectedJobPosition = useMemo(
    () => candidate?.requisition?.jobPositions.find((jp) => jp.id === jobPositionId) || null,
    [candidate, jobPositionId]
  );

  const fullName = candidate ? `${candidate.firstName} ${candidate.lastName}` : "";
  const positionTitle = candidate?.requisition?.jobRole?.roleName || "";
  const departmentName = candidate?.requisition?.department?.departmentName || "";
  const reportsToName = candidate?.requisition?.reportsTo?.name || "";

  useEffect(() => {
    if (!candidateId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [candRes, tplRes, orgRes] = await Promise.all([
          api.get<{ success: boolean; candidate: Candidate }>(`/api/candidates/${candidateId}`),
          api.get<{ success: boolean; templates: OfferTemplate[] }>("/api/offer-templates"),
          api.get<{ success: boolean; organizations: RequisitionOrganization[] }>("/api/requisition-organizations"),
        ]);

        if (candRes.success) {
          const cand = candRes.candidate;
          setCandidate(cand);
          if (cand.jobPosition) setJobPositionId(cand.jobPosition.id);
          if (cand.requisition?.jobPositions?.length === 1 && !cand.jobPosition) {
            setJobPositionId(cand.requisition.jobPositions[0].id);
          }
          const salaryStr = formatSalary(cand.requisition?.salaryRangeMin ?? null, cand.requisition?.salaryRangeMax ?? null);
          setSalary(salaryStr);
          if (cand.requisition?.startDate) {
            setStartDate(formatDateInput(cand.requisition.startDate));
          }
        }

        if (tplRes.success) {
          const activeTemplates = tplRes.templates.filter((t) => t.isActive);
          setTemplates(activeTemplates);
          const defaultTpl = activeTemplates.find((t) => t.isDefault);
          if (defaultTpl) setSelectedTemplateId(defaultTpl.id);
        }

        let orgs: RequisitionOrganization[] = [];
        if (orgRes.success) {
          orgs = orgRes.organizations;
          setOrganizations(orgs);
          const defaultOrg = orgs.find((o) => o.isDefault);
          if (defaultOrg) {
            setCompanyName(defaultOrg.companyName);
            setOfferDeadline(addDays(new Date(), defaultOrg.defaultOfferExpiryDays));
          }
        }

        if (currentUser) {
          setSenderName(currentUser.name);
          setSenderTitle(currentUser.jobRole || "");
        }

        // Check for existing draft offer and load it
        const offerRes = await api.get<{ success: boolean; offers: Offer[] }>(`/api/offers?candidateId=${candidateId}`);
        if (offerRes.success && offerRes.offers.length > 0) {
          const draft = offerRes.offers.find((o) => o.status === "DRAFT");
          if (draft) {
            const detailRes = await api.get<{ success: boolean; offer: Offer }>(`/api/offers/${draft.id}`);
            if (detailRes.success) {
              const offer = detailRes.offer;
              setOfferId(offer.id);
              if (offer.offerTemplate?.id) {
                setSelectedTemplateId(offer.offerTemplate.id);
              }
              if (offer.salaryOffered) {
                setSalary(`$${Number(offer.salaryOffered).toLocaleString()}`);
              }
              if (offer.startDate) {
                setStartDate(formatDateInput(offer.startDate));
              }
              if (offer.expiryDate) {
                setOfferDeadline(formatDateInput(offer.expiryDate));
              }
              if (offer.requisitionOrganization?.companyName) {
                setCompanyName(offer.requisitionOrganization.companyName);
              } else if (offer.requisitionOrganization?.id) {
                const org = orgs.find((o) => o.id === offer.requisitionOrganization!.id);
                if (org) setCompanyName(org.companyName);
              }
              if (offer.offerLetterContent) {
                setEditedLetterContent(plainTextToHtml(offer.offerLetterContent));
              }
              setStep("fill-form");
            }
          }
        }
      } catch (err: any) {
        console.error("Failed to load data:", err);
        toast.error(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [candidateId, currentUser]);

  useEffect(() => {
    if (selectedJobPosition) {
      if (selectedJobPosition.location) setLocation(selectedJobPosition.location);
      if (selectedJobPosition.employmentType) setEmploymentType(selectedJobPosition.employmentType);
    }
  }, [selectedJobPosition]);

  const handleContinue = () => {
    if (!selectedTemplateId) {
      toast.error("Please select an offer template");
      return;
    }
    setStep("fill-form");
    setActiveFieldIndex(0);
  };

  const handleChangeTemplate = () => {
    setStep("select-template");
    setIsEditingContent(false);
    setEditedLetterContent(null);
  };

  const scrollPreviewToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    const container = previewRef.current;
    if (section && container) {
      const top = section.offsetTop - container.offsetTop - 16;
      container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }
  };

  const focusFieldAtIndex = (index: number) => {
    const field = NAV_FIELDS[index];
    if (!field) return;
    setActiveFieldIndex(index);
    scrollPreviewToSection(field.sectionId);

    // Focus the actual input if editable
    if (field.editable) {
      const inputEl = fieldRefs.current[index];
      if (inputEl) {
        inputEl.focus();
        inputEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      // For read-only fields, just scroll the form to show them
      const el = document.getElementById(`field-${field.key}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handlePrevious = () => {
    const newIndex = activeFieldIndex > 0 ? activeFieldIndex - 1 : NAV_FIELDS.length - 1;
    focusFieldAtIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = activeFieldIndex < NAV_FIELDS.length - 1 ? activeFieldIndex + 1 : 0;
    focusFieldAtIndex(newIndex);
  };

  const getFieldValue = (key: string): string => {
    switch (key) {
      case "jobPosition": return selectedJobPosition ? `${selectedJobPosition.positionCode}${selectedJobPosition.location ? ` - ${selectedJobPosition.location}` : ""}` : "";
      case "candidateName": return fullName;
      case "positionTitle": return positionTitle;
      case "companyName": return companyName;
      case "department": return departmentName;
      case "location": return location;
      case "employmentType": return employmentType;
      case "startDate": return startDate;
      case "salary": return salary;
      case "reportingTo": return reportsToName;
      case "award": return award;
      case "probationPeriod": return probationPeriod;
      case "noticePeriod": return noticePeriod;
      case "restraintArea": return restraintArea;
      case "restraintPeriod": return restraintPeriod;
      case "entitlements": return entitlements.join(", ");
      case "offerDeadline": return offerDeadline;
      case "employeeAddress": return employeeAddress;
      case "abn": return abn;
      case "companyLocation": return companyLocation;
      case "baseHourlyRate": return baseHourlyRate;
      case "hoursOfWork": return hoursOfWork;
      case "directorsName": return directorsName;
      case "remuneration": return remuneration;
      case "employmentStatus": return employmentStatus;
      case "senderName": return senderName;
      case "senderTitle": return senderTitle;
      default: return "";
    }
  };

  const generateOfferLetter = () => {
    if (editedLetterContent !== null) return editedLetterContent;
    let content: string;
    if (!selectedTemplate?.bodyContent) {
      content = buildDefaultLetter();
    } else {
      content = selectedTemplate.bodyContent;
      const replacements: Record<string, string> = {
        "{{candidateName}}": fullName,
        "{{employeeName}}": fullName,
        "{{firstName}}": candidate?.firstName || "",
        "{{lastName}}": candidate?.lastName || "",
        "{{position}}": positionTitle,
        "{{positionTitle}}": positionTitle,
        "{{department}}": departmentName,
        "{{location}}": location,
        "{{company}}": companyName,
        "{{companyName}}": companyName,
        "{{salary}}": salary,
        "{{startDate}}": startDate,
        "{{expiryDate}}": offerDeadline,
        "{{senderName}}": senderName,
        "{{senderTitle}}": senderTitle,
        "{{award}}": award,
        "{{probationPeriod}}": probationPeriod,
        "{{noticePeriod}}": noticePeriod,
        "{{restraintArea}}": restraintArea,
        "{{restraintPeriod}}": restraintPeriod,
        "{{entitlements}}": entitlements.join("\n"),
        "{{offerDeadline}}": offerDeadline,
        "{{reportingTo}}": reportsToName,
        "{{employeeAddress}}": employeeAddress,
        "{{abn}}": abn,
        "{{companyLocation}}": companyLocation,
        "{{baseHourlyRate}}": baseHourlyRate,
        "{{hoursOfWork}}": hoursOfWork,
        "{{directorsName}}": directorsName,
        "{{remuneration}}": remuneration,
        "{{employmentStatus}}": employmentStatus,
        "{{employmentType}}": employmentType,
      };
      Object.entries(replacements).forEach(([key, value]) => {
        content = content.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), value || "");
      });
    }
    return plainTextToHtml(content);
  };

  const pv = (value: string, placeholder: string) => value.trim() || `[${placeholder}]`;

  const plainTextToHtml = (text: string): string => {
    if (!text) return "";
    // If already HTML, return as-is
    if (text.trim().startsWith("<") && text.trim().endsWith(">")) return text;
    return text
      .split("\n")
      .map((line) => (line.trim() ? `<p>${line}</p>` : "<p><br></p>"))
      .join("");
  };
  const isHidden = (key: string) => hiddenFields.has(key);

  const buildDefaultLetter = () => {
    const lines: string[] = [];

    // Greeting
    lines.push(`Dear ${fullName},`);
    lines.push("");
    lines.push(`We are pleased to offer you the position of ${isHidden("positionTitle") ? "[positionTitle]" : pv(positionTitle, "positionTitle")} at ${companyName || "our company"}.`);
    lines.push("");

    // Position Details section
    const positionLines: string[] = [];
    if (!isHidden("positionTitle")) positionLines.push(`- Position: ${pv(positionTitle, "positionTitle")}`);
    if (!isHidden("department")) positionLines.push(`- Department: ${pv(departmentName, "department")}`);
    if (!isHidden("location")) positionLines.push(`- Location: ${pv(location, "location")}`);
    if (!isHidden("employmentType")) positionLines.push(`- Employment Type: ${pv(employmentType, "employmentType")}`);
    if (!isHidden("startDate")) positionLines.push(`- Start Date: ${pv(startDate, "startDate")}`);
    if (!isHidden("salary")) positionLines.push(`- Salary: ${pv(salary, "salary")} per annum`);
    if (!isHidden("reportingTo")) positionLines.push(`- Reporting To: ${pv(reportsToName, "reportingTo")}`);
    if (!isHidden("award")) positionLines.push(`- Award: ${pv(award, "award")}`);
    if (positionLines.length > 0) {
      lines.push("Position Details:");
      positionLines.forEach((l) => lines.push(l));
      lines.push("");
    }

    // Employment Terms section
    const termsLines: string[] = [];
    if (!isHidden("probationPeriod")) termsLines.push(`- Probation Period: ${pv(probationPeriod, "probationPeriod")}`);
    if (!isHidden("noticePeriod")) termsLines.push(`- Notice Period: ${pv(noticePeriod, "noticePeriod")}`);
    if (!isHidden("restraintArea")) termsLines.push(`- Restraint Area: ${pv(restraintArea, "restraintArea")}`);
    if (!isHidden("restraintPeriod")) termsLines.push(`- Restraint Period: ${pv(restraintPeriod, "restraintPeriod")}`);
    if (termsLines.length > 0) {
      lines.push("Employment Terms:");
      termsLines.forEach((l) => lines.push(l));
      lines.push("");
    }

    // Entitlements section
    const entitlementLines: string[] = [];
    if (!isHidden("entitlements")) {
      if (entitlements.length > 0) {
        entitlements.forEach((item) => {
          if (item.trim()) entitlementLines.push(`• ${item.trim()}`);
        });
      } else {
        entitlementLines.push("• [entitlements]");
      }
    }
    if (entitlementLines.length > 0) {
      lines.push("Entitlements:");
      entitlementLines.forEach((l) => lines.push(l));
      lines.push("");
    }

    // Contingent & closing (always shown)
    lines.push("This offer is contingent upon:");
    lines.push("- Satisfactory completion of background checks");
    lines.push("- Proof of right to work in Australia");
    lines.push("- Signing of employment contract and company policies");
    lines.push("");
    lines.push(`Please sign below to accept this offer by ${pv(offerDeadline, "offerDeadline")}.`);
    lines.push("");
    lines.push("We look forward to welcoming you to our team!");
    lines.push("");
    lines.push("Best regards,");
    if (!isHidden("senderName")) lines.push(pv(senderName, "senderName"));
    if (!isHidden("senderTitle")) lines.push(pv(senderTitle, "senderTitle"));
    return lines.join("\n");
  };

  const buildOfferPayload = () => {
    return {
      candidateId,
      requisitionId: candidate?.requisition?.id || undefined,
      offerTemplateId: selectedTemplateId || undefined,
      requisitionOrganizationId: organizations.find((o) => o.companyName === companyName)?.id || undefined,
      salaryOffered: salary ? parseFloat(salary.replace(/[^0-9.]/g, "")) : undefined,
      startDate: startDate || undefined,
      expiryDate: offerDeadline || undefined,
      location: location || undefined,
      offerLetterContent: generateOfferLetter(),
    };
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const payload = buildOfferPayload();
      if (offerId) {
        // Update existing draft
        const res = await api.put<{ success: boolean }>(`/api/offers/${offerId}`, payload);
        if (res.success) {
          toast.success("Draft updated");
        }
      } else {
        // Create new draft
        const res = await api.post<{ success: boolean; offer: { id: number } }>("/api/offers", payload);
        if (res.success) {
          setOfferId(res.offer.id);
          toast.success("Offer saved as draft");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save offer");
    } finally {
      setSavingDraft(false);
    }
  };

  const validateRequiredFields = (): string[] => {
    const missingFields = [];
    if (!salary || !parseFloat(salary.replace(/[^0-9.]/g, ""))) missingFields.push("Salary");
    if (!startDate) missingFields.push("Start Date");
    if (!offerDeadline) missingFields.push("Offer Deadline");
    return missingFields;
  };

  const handleSendOffer = () => {
    if (!offerId) {
      toast.error("Please save the offer first");
      return;
    }
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.join(", ")}`);
      return;
    }
    setShowSendModal(true);
  };

  const handleSaveAndSend = async () => {
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.join(", ")}`);
      return;
    }
    setSendingOffer(true);
    try {
      const payload = buildOfferPayload();
      let currentOfferId = offerId;
      if (currentOfferId) {
        await api.put<{ success: boolean }>(`/api/offers/${currentOfferId}`, payload);
      } else {
        const res = await api.post<{ success: boolean; offer: { id: number } }>("/api/offers", payload);
        if (res.success) {
          currentOfferId = res.offer.id;
          setOfferId(currentOfferId);
        }
      }
      if (currentOfferId) {
        const sendRes = await api.post<{ success: boolean }>(`/api/offers/${currentOfferId}/send`, {});
        if (sendRes.success) {
          toast.success("Offer sent to candidate");
          navigate(`/recruitment/candidates/${candidateId}`);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save and send offer");
    } finally {
      setSendingOffer(false);
    }
  };

  const confirmSendOffer = async () => {
    if (!offerId) return;
    setSendingOffer(true);
    try {
      // Save draft first to ensure latest data is persisted
      const payload = buildOfferPayload();
      await api.put<{ success: boolean }>(`/api/offers/${offerId}`, payload);

      const res = await api.post<{ success: boolean }>(`/api/offers/${offerId}/send`, {});
      if (res.success) {
        toast.success("Offer sent to candidate");
        setShowSendModal(false);
        navigate(`/recruitment/candidates/${candidateId}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to send offer");
    } finally {
      setSendingOffer(false);
    }
  };

  const toggleHidden = (fieldKey: string) => {
    setHiddenFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) next.delete(fieldKey);
      else next.add(fieldKey);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <p className="text-sm font-medium text-gray-500">Candidate not found</p>
        <Button
          className="mt-4 bg-slate-950 hover:bg-slate-900 text-white rounded-xl text-sm font-bold px-5 h-10"
          onClick={() => navigate("/recruitment")}
        >
          Back to Recruitment
        </Button>
      </div>
    );
  }

  // ─── STEP 1: SELECT TEMPLATE ───
  if (step === "select-template") {
    return (
      <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
        <button
          onClick={() => navigate(`/recruitment/candidates/${candidateId}`)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
        >
          <ChevronLeft size={18} />
          <span className="text-sm font-medium">Back to Candidate</span>
        </button>

        <div className="flex items-center gap-3">
          <FileText size={28} className="text-gray-900" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Create Offer - {fullName}</h1>
            <p className="text-gray-500 text-sm font-medium">{positionTitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Templates */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">Select Offer Template</h4>
                <p className="text-gray-500 text-sm font-medium mt-0.5">Choose a template to start creating the offer</p>
              </div>

              {templates.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm font-medium text-gray-500">No active offer templates available.</p>
                  <Button
                    className="mt-3 bg-slate-950 hover:bg-slate-900 text-white rounded-xl text-xs font-bold px-4 h-9"
                    onClick={() => navigate("/recruitment/settings/offer-templates/new")}
                  >
                    Create Template
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplateId(template.id)}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        selectedTemplateId === template.id
                          ? "border-blue-400 bg-blue-50/30 ring-1 ring-blue-400"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-gray-900 truncate">{template.name}</p>
                            {template.isSystem && (
                              <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700 shrink-0">
                                {template.templateType === "CASUAL" ? "Casual" : template.templateType === "PERMANENT" ? "Permanent" : "System"}
                              </span>
                            )}
                            {template.isDefault && (
                              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 shrink-0">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 font-medium mt-1 truncate">{template.subject || ""}</p>
                        </div>
                        <FileText
                          size={18}
                          className={`shrink-0 mt-0.5 ${selectedTemplateId === template.id ? "text-blue-500" : "text-gray-300"}`}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {templates.length > 0 && (
                <div className="flex items-center justify-end pt-2">
                  <Button
                    className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-11 rounded-xl text-sm font-bold px-6"
                    onClick={handleContinue}
                    disabled={!selectedTemplateId}
                  >
                    <span>Continue</span>
                    <ChevronLeft size={16} className="rotate-180" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right - Candidate Information */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
              <h4 className="font-bold text-gray-900 text-base">Candidate Information</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <User size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Name</span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{fullName}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Mail size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Email</span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{candidate.email}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Phone size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Phone</span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{candidate.phone || "—"}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Current Position</span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{candidate.currentPosition || "—"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 2: FILL FORM ───
  return (
    <div className="flex flex-col space-y-4 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruitment/candidates/${candidateId}`)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Candidate</span>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <FileText size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Create Offer - {fullName}</h1>
          <p className="text-gray-500 text-sm font-medium">{positionTitle}</p>
        </div>
      </div>

      {/* Offer Details Header */}
      <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-bold text-gray-900 text-base">Offer Details</h4>
            <p className="text-gray-500 text-sm font-medium mt-0.5">Template: {selectedTemplate?.name}</p>
          </div>
          <Button
            variant="outline"
            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-9 rounded-xl text-xs font-bold px-4"
            onClick={handleChangeTemplate}
          >
            Change Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <div className="space-y-4">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-9 rounded-xl text-xs font-bold px-4 flex items-center gap-1.5"
              onClick={handlePrevious}
            >
              <ChevronUp size={14} />
              Previous
            </Button>
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-9 rounded-xl text-xs font-bold px-4 flex items-center gap-1.5"
              onClick={handleNext}
            >
              Next
              <ChevronDown size={14} />
            </Button>
            <span className="text-xs text-gray-400 font-medium ml-1">
              {NAV_FIELDS[activeFieldIndex]?.label}
            </span>
          </div>

          {/* Confirm Job Position */}
          {candidate.requisition?.jobPositions && candidate.requisition.jobPositions.length > 1 && (
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
              <div className="p-3 rounded-xl bg-blue-50/50 border border-blue-100 space-y-2.5">
                <div>
                  <p className="text-sm font-bold text-blue-900">Confirm Job Position</p>
                  <p className="text-xs text-blue-600 font-medium">Confirm the job position for this offer.</p>
                </div>
                <div className="relative">
                  <select
                    ref={(el) => { fieldRefs.current[0] = el; }}
                    value={jobPositionId ?? ""}
                    onChange={(e) => setJobPositionId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full bg-white border border-blue-200 rounded-xl h-10 text-sm font-medium px-3 pr-10 focus:ring-2 focus:ring-blue-100 focus:border-blue-300 appearance-none cursor-pointer"
                  >
                    <option value="">Select job position...</option>
                    {candidate.requisition.jobPositions.map((jp) => (
                      <option key={jp.id} value={jp.id}>
                        {jp.positionCode}{jp.location ? ` - ${jp.location}` : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronLeft size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 rotate-270 pointer-events-none" />
                </div>
                {selectedJobPosition && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md font-bold">
                      {selectedJobPosition.location || "No location"}
                    </span>
                    <span className="text-blue-400">•</span>
                    <span className="text-blue-700 font-medium">{selectedJobPosition.positionCode}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="bg-white p-4 sm:p-5 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <FieldRow fieldKey="candidateName" label="Candidate Name" isActive={activeFieldIndex === 1} isHidden={isHidden("candidateName")} onToggleHidden={toggleHidden}>
                <Input value={fullName} readOnly className="bg-gray-100 border-gray-200 rounded-xl h-10 text-sm font-medium text-gray-600" />
              </FieldRow>
              <FieldRow fieldKey="employeeAddress" label="Employee Address" isActive={activeFieldIndex === 2} isHidden={isHidden("employeeAddress")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[2] = el; }}
                  placeholder="Enter employee address"
                  value={employeeAddress}
                  onChange={(e) => setEmployeeAddress(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 2 */}
              <FieldRow fieldKey="positionTitle" label="Position Title" isActive={activeFieldIndex === 3} isHidden={isHidden("positionTitle")} onToggleHidden={toggleHidden}>
                <Input value={positionTitle} readOnly className="bg-gray-100 border-gray-200 rounded-xl h-10 text-sm font-medium text-gray-600" />
              </FieldRow>
              <FieldRow fieldKey="companyName" label="Company Name" isActive={activeFieldIndex === 4} isHidden={isHidden("companyName")} onToggleHidden={toggleHidden} required>
                <Select value={companyName} onValueChange={setCompanyName}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                    <SelectValue placeholder="Select company..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.companyName}>{org.companyName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>

              {/* Row 3 */}
              <FieldRow fieldKey="abn" label="ABN" isActive={activeFieldIndex === 5} isHidden={isHidden("abn")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[5] = el; }}
                  placeholder="12 345 678 901"
                  value={abn}
                  onChange={(e) => setAbn(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="companyLocation" label="Company Location" isActive={activeFieldIndex === 6} isHidden={isHidden("companyLocation")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[6] = el; }}
                  placeholder="Enter company location"
                  value={companyLocation}
                  onChange={(e) => setCompanyLocation(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 4 */}
              <FieldRow fieldKey="department" label="Department" isActive={activeFieldIndex === 7} isHidden={isHidden("department")} onToggleHidden={toggleHidden}>
                <Input value={departmentName} readOnly className="bg-gray-100 border-gray-200 rounded-xl h-10 text-sm font-medium text-gray-600" />
              </FieldRow>
              <FieldRow fieldKey="location" label="Location" isActive={activeFieldIndex === 8} isHidden={isHidden("location")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[8] = el; }}
                  placeholder="Enter location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 5 */}
              <FieldRow fieldKey="employmentType" label="Employment Type" isActive={activeFieldIndex === 9} isHidden={isHidden("employmentType")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[9] = el; }}
                  placeholder="Full Time"
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="employmentStatus" label="Employment Status" isActive={activeFieldIndex === 10} isHidden={isHidden("employmentStatus")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[10] = el; }}
                  placeholder="Full-time / Part-time"
                  value={employmentStatus}
                  onChange={(e) => setEmploymentStatus(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 6 */}
              <FieldRow fieldKey="startDate" label="Start Date" isActive={activeFieldIndex === 11} isHidden={isHidden("startDate")} onToggleHidden={toggleHidden} required>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      ref={(el) => { fieldRefs.current[11] = el; }}
                      variant="outline"
                      className={`w-full justify-start text-left font-normal h-10 rounded-xl text-sm bg-gray-50 border-gray-200 hover:bg-gray-100 ${!startDate ? "text-gray-400" : "text-gray-900"}`}
                    >
                      <Calendar size={16} className="mr-2 text-gray-400 shrink-0" />
                      {startDate
                        ? formatSydneyDateLong(new Date(startDate + "T00:00:00"))
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPrimitive
                      mode="single"
                      selected={startDate ? new Date(startDate + "T00:00:00") : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, "0");
                          const d = String(date.getDate()).padStart(2, "0");
                          setStartDate(`${y}-${m}-${d}`);
                        } else {
                          setStartDate("");
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </FieldRow>
              <FieldRow fieldKey="salary" label="Salary" isActive={activeFieldIndex === 12} isHidden={isHidden("salary")} onToggleHidden={toggleHidden} required>
                <Input
                  ref={(el) => { fieldRefs.current[12] = el; }}
                  placeholder="$90,000 - $110,000"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 7 */}
              <FieldRow fieldKey="reportingTo" label="Reporting To" isActive={activeFieldIndex === 13} isHidden={isHidden("reportingTo")} onToggleHidden={toggleHidden}>
                <Input value={reportsToName} readOnly className="bg-gray-100 border-gray-200 rounded-xl h-10 text-sm font-medium text-gray-600" />
              </FieldRow>
              <FieldRow fieldKey="award" label="Award" isActive={activeFieldIndex === 14} isHidden={isHidden("award")} onToggleHidden={toggleHidden}>
                <div className="relative">
                  <select
                    ref={(el) => { fieldRefs.current[14] = el; }}
                    value={award}
                    onChange={(e) => setAward(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl h-10 text-sm font-medium px-3 pr-10 focus:ring-2 focus:ring-blue-100 focus:border-blue-300 appearance-none cursor-pointer"
                  >
                    <option value="">Select award...</option>
                    <option value="Modern Award">Modern Award</option>
                    <option value="Enterprise Agreement">Enterprise Agreement</option>
                    <option value="Individual Contract">Individual Contract</option>
                  </select>
                  <ChevronLeft size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 rotate-270 pointer-events-none" />
                </div>
              </FieldRow>

              {/* Row 8 */}
              <FieldRow fieldKey="baseHourlyRate" label="Base Hourly Rate" isActive={activeFieldIndex === 15} isHidden={isHidden("baseHourlyRate")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[15] = el; }}
                  placeholder="$35.00"
                  value={baseHourlyRate}
                  onChange={(e) => setBaseHourlyRate(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="hoursOfWork" label="Hours of Work" isActive={activeFieldIndex === 16} isHidden={isHidden("hoursOfWork")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[16] = el; }}
                  placeholder="e.g. 38 hours per week, Monday to Friday"
                  value={hoursOfWork}
                  onChange={(e) => setHoursOfWork(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 9 */}
              <FieldRow fieldKey="probationPeriod" label="Probation Period" isActive={activeFieldIndex === 17} isHidden={isHidden("probationPeriod")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[17] = el; }}
                  placeholder="Enter probation period"
                  value={probationPeriod}
                  onChange={(e) => setProbationPeriod(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="noticePeriod" label="Notice Period" isActive={activeFieldIndex === 18} isHidden={isHidden("noticePeriod")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[18] = el; }}
                  placeholder="Enter notice period"
                  value={noticePeriod}
                  onChange={(e) => setNoticePeriod(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 10 */}
              <FieldRow fieldKey="restraintArea" label="Restraint Area" isActive={activeFieldIndex === 19} isHidden={isHidden("restraintArea")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[19] = el; }}
                  placeholder="Enter restraint area"
                  value={restraintArea}
                  onChange={(e) => setRestraintArea(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="restraintPeriod" label="Restraint Period" isActive={activeFieldIndex === 20} isHidden={isHidden("restraintPeriod")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[20] = el; }}
                  placeholder="Enter restraint period"
                  value={restraintPeriod}
                  onChange={(e) => setRestraintPeriod(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 11 */}
              <FieldRow fieldKey="entitlements" label="Entitlements" isActive={activeFieldIndex === 21} isHidden={isHidden("entitlements")} onToggleHidden={toggleHidden}>
                <div className="space-y-2">
                  {entitlements.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        ref={idx === 0 ? (el) => { fieldRefs.current[21] = el; } : undefined}
                        value={item}
                        onChange={(e) => {
                          const next = [...entitlements];
                          next[idx] = e.target.value;
                          setEntitlements(next);
                        }}
                        placeholder="Enter entitlement..."
                        className="bg-gray-50 border-gray-200 rounded-xl h-9 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                      />
                      <button
                        onClick={() => {
                          const next = entitlements.filter((_, i) => i !== idx);
                          setEntitlements(next);
                        }}
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                        title="Remove entitlement"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-8 rounded-xl text-xs font-bold px-3 flex items-center gap-1.5 w-fit"
                    onClick={() => setEntitlements((prev) => [...prev, ""])}
                  >
                    <Plus size={14} />
                    Add Entitlement
                  </Button>
                </div>
              </FieldRow>
              <FieldRow fieldKey="offerDeadline" label="Offer Deadline" isActive={activeFieldIndex === 22} isHidden={isHidden("offerDeadline")} onToggleHidden={toggleHidden} required>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      ref={(el) => { fieldRefs.current[22] = el; }}
                      variant="outline"
                      className={`w-full justify-start text-left font-normal h-10 rounded-xl text-sm bg-gray-50 border-gray-200 hover:bg-gray-100 ${!offerDeadline ? "text-gray-400" : "text-gray-900"}`}
                    >
                      <Calendar size={16} className="mr-2 text-gray-400 shrink-0" />
                      {offerDeadline
                        ? formatSydneyDateLong(new Date(offerDeadline + "T00:00:00"))
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPrimitive
                      mode="single"
                      selected={offerDeadline ? new Date(offerDeadline + "T00:00:00") : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, "0");
                          const d = String(date.getDate()).padStart(2, "0");
                          setOfferDeadline(`${y}-${m}-${d}`);
                        } else {
                          setOfferDeadline("");
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </FieldRow>

              {/* Row 12 */}
              <FieldRow fieldKey="remuneration" label="Remuneration Package" isActive={activeFieldIndex === 23} isHidden={isHidden("remuneration")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[23] = el; }}
                  placeholder="$90,000 per annum inclusive of super"
                  value={remuneration}
                  onChange={(e) => setRemuneration(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="directorsName" label="Directors Name" isActive={activeFieldIndex === 24} isHidden={isHidden("directorsName")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[24] = el; }}
                  placeholder="Enter directors name"
                  value={directorsName}
                  onChange={(e) => setDirectorsName(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>

              {/* Row 13 */}
              <FieldRow fieldKey="senderName" label="Sender Name" isActive={activeFieldIndex === 25} isHidden={isHidden("senderName")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[25] = el; }}
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
              <FieldRow fieldKey="senderTitle" label="Sender Title" isActive={activeFieldIndex === 26} isHidden={isHidden("senderTitle")} onToggleHidden={toggleHidden}>
                <Input
                  ref={(el) => { fieldRefs.current[26] = el; }}
                  value={senderTitle}
                  onChange={(e) => setSenderTitle(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </FieldRow>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white p-4 sm:p-5 rounded-3xl border border-gray-100 shadow-sm space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold"
                onClick={handleSaveAndSend}
                disabled={savingDraft || sendingOffer}
              >
                {sendingOffer ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                <span>Save & Send</span>
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold"
                  onClick={handleSaveDraft}
                  disabled={savingDraft || sendingOffer}
                >
                  {savingDraft ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  <span>{offerId ? "Update Draft" : "Save Draft"}</span>
                </Button>
                <Button
                  variant="outline"
                  className={`flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold ${
                    offerId
                      ? "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                      : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={handleSendOffer}
                  disabled={savingDraft || sendingOffer || !offerId}
                >
                  {sendingOffer ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                  <span>Send to Candidate</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Live Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Preview</h4>
            {!isEditingContent && (
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-9 rounded-xl text-xs font-bold px-4 flex items-center gap-2"
                onClick={() => {
                  const letter = generateOfferLetter();
                  setEditedLetterContent(plainTextToHtml(letter));
                  setIsEditingContent(true);
                }}
              >
                <Pencil size={14} />
                Edit Content
              </Button>
            )}
          </div>

          <div
            ref={previewRef}
            className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden max-h-[calc(100vh-200px)] overflow-y-auto"
          >
            {isEditingContent ? (
              <div className="p-5 space-y-3">
                <CustomEditor
                  value={editedLetterContent || ""}
                  onChange={(value) => setEditedLetterContent(value)}
                  className="min-h-[500px]"
                  placeholder="Edit the offer letter content..."
                />
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                    onClick={() => {
                      setEditedLetterContent(null);
                      setIsEditingContent(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5 flex items-center gap-2"
                    onClick={() => setIsEditingContent(false)}
                  >
                    <Eye size={14} />
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <style>{`
                  .offer-letter-preview p {
                    margin: 0;
                    padding: 0;
                  }
                  .offer-letter-preview p + p {
                    margin-top: 0.5em;
                  }
                  .offer-letter-preview br {
                    display: block;
                    content: "";
                    margin-top: 0.5em;
                  }
                  .offer-letter-preview ul, .offer-letter-preview ol {
                    margin: 0.5em 0;
                    padding-left: 1.5em;
                  }
                  .offer-letter-preview h1, .offer-letter-preview h2, .offer-letter-preview h3,
                  .offer-letter-preview h4, .offer-letter-preview h5, .offer-letter-preview h6 {
                    margin: 0.5em 0;
                    font-weight: 600;
                  }
                `}</style>
                <div className="p-6 sm:p-8">
                  <div className="border border-gray-100 rounded-xl p-6 sm:p-8 bg-gray-50/30 min-h-[500px]">
                    <div
                      className="text-sm text-gray-800 font-medium leading-relaxed offer-letter-preview"
                      dangerouslySetInnerHTML={{ __html: generateOfferLetter() }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Send Offer Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle className="text-lg font-bold text-gray-900">Send Offer to Candidate</DialogTitle>
            <DialogDescription className="text-sm text-gray-500 font-medium">
              Confirm sending offer to {fullName}
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-5">
            <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
              <p className="text-sm text-blue-800 font-medium leading-relaxed">
                The offer will be sent to <span className="font-bold">{candidate?.email}</span>. The candidate will receive a unique link to review, sign, and accept or reject the offer.
              </p>
            </div>

            <div>
              <p className="text-sm font-bold text-gray-900 mb-2">This action will:</p>
              <ul className="space-y-1.5">
                <li className="text-sm text-gray-600 font-medium flex items-start gap-2">
                  <span className="text-gray-400 mt-1.5">•</span>
                  Send offer email to candidate
                </li>
                <li className="text-sm text-gray-600 font-medium flex items-start gap-2">
                  <span className="text-gray-400 mt-1.5">•</span>
                  Update candidate status to "Offer Sent"
                </li>
                <li className="text-sm text-gray-600 font-medium flex items-start gap-2">
                  <span className="text-gray-400 mt-1.5">•</span>
                  Generate unique acceptance link
                </li>
                <li className="text-sm text-gray-600 font-medium flex items-start gap-2">
                  <span className="text-gray-400 mt-1.5">•</span>
                  Start tracking candidate activity
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-end gap-3 pt-1">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                onClick={() => setShowSendModal(false)}
                disabled={sendingOffer}
              >
                Cancel
              </Button>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5 flex items-center gap-2"
                onClick={confirmSendOffer}
                disabled={sendingOffer}
              >
                {sendingOffer ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                <span>Send Offer</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateOffer;
